import React, { useState } from "react";
import { Mail, ChevronRight, Info } from "lucide-react";
import { cn } from "../../components/ui/utils";

// Import template components
import TicketCreatedEmail from "./email-templates/TicketCreatedEmail";
import TicketAssignedEmail from "./email-templates/TicketAssignedEmail";
import NewMessageEmail from "./email-templates/NewMessageEmail";
import TicketClosedEmail from "./email-templates/TicketClosedEmail";
import StatusChangedEmail from "./email-templates/StatusChangedEmail";
import TicketAcknowledgedEmail from "./email-templates/TicketAcknowledgedEmail";
import TicketResolvedEmail from "./email-templates/TicketResolvedEmail";
import AdminNewTicketEmail from "./email-templates/AdminNewTicketEmail";

const templates = [
    {
        id: "admin-new-ticket",
        name: "Admin: New Ticket Attention",
        description: "Sent to Service Desk Admins when a new ticket is submitted and needs assignment.",
        component: <AdminNewTicketEmail />
    },
    {
        id: "ticket-created",
        name: "Ticket Created",
        description: "Sent to the requester after a ticket is successfully created.",
        component: <TicketCreatedEmail />
    },
    {
        id: "ticket-assigned",
        name: "Ticket Assigned",
        description: "Sent to an agent when a ticket is assigned to them.",
        component: <TicketAssignedEmail />
    },
    {
        id: "new-message",
        name: "New Message",
        description: "Sent when a new message is added to a ticket.",
        component: <NewMessageEmail />
    },
    {
        id: "ticket-acknowledged",
        name: "Ticket Acknowledged",
        description: "Sent to the requester when an agent starts working on the ticket.",
        component: <TicketAcknowledgedEmail />
    },
    {
        id: "status-changed",
        name: "Status Changed",
        description: "Sent when the status of a ticket is updated.",
        component: <StatusChangedEmail />
    },
    {
        id: "ticket-resolved",
        name: "Ticket Resolved",
        description: "Sent to the requester when the issue is marked as resolved.",
        component: <TicketResolvedEmail />
    },
    {
        id: "ticket-closed",
        name: "Ticket Closed",
        description: "Sent when a ticket is permanently closed.",
        component: <TicketClosedEmail />
    }
];

const EmailTemplatesLayout: React.FC = () => {
    // Use internal state instead of URL for selection
    const [selectedId, setSelectedId] = useState<string | null>(templates[0].id);
    const selectedTemplate = templates.find(t => t.id === selectedId);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Email Template Previews</h1>
                <p className="text-sm text-muted-foreground">Review and test system email designs</p>
            </div>

            <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-200px)]">
                {/* Left Pane - Template List */}
                <div className="w-full md:w-80 flex flex-col bg-white border rounded-xl overflow-hidden shadow-sm">
                    <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Templates</h3>
                            <Mail className="w-4 h-4 text-gray-400" />
                        </div>

                        <div className="space-y-2">
                            {templates.map((template) => (
                                <button
                                    key={template.id}
                                    onClick={() => setSelectedId(template.id)}
                                    className={cn(
                                        "w-full text-left p-4 rounded-lg transition-all border group text-left",
                                        selectedId === template.id
                                            ? "bg-blue-50/50 border-blue-200 ring-1 ring-blue-500"
                                            : "hover:bg-gray-50/50 border-transparent hover:border-gray-200"
                                    )}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <div className={cn(
                                            "font-medium text-left",
                                            selectedId === template.id ? "text-blue-700" : "text-gray-900"
                                        )}>
                                            {template.name}
                                        </div>
                                        <ChevronRight className={cn(
                                            "w-4 h-4 transition-transform group-hover:translate-x-1",
                                            selectedId === template.id ? "text-blue-500" : "text-gray-400"
                                        )} />
                                    </div>
                                    <div className="text-xs text-gray-500 line-clamp-2 leading-relaxed text-left">
                                        {template.description}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Pane - Content Preview */}
                <div className="flex-1 overflow-y-auto bg-white border rounded-xl p-0 shadow-sm custom-scrollbar relative">
                    {selectedTemplate ? (
                        <div key={selectedId} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {selectedTemplate.component}
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="h-16 w-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 mb-4 border border-dashed border-gray-300">
                                <Mail size={32} />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a template</h3>
                            <p className="text-gray-500 max-w-xs">
                                Choose a template from the left sidebar to see a live preview of how it appears to users.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3 items-start">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                    <strong>Note:</strong> These previews use mock data to demonstrate the visual layout. 
                    Links within the previews are currently disabled.
                </p>
            </div>
        </div>
    );
};

export default EmailTemplatesLayout;
